import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { getDiffDetail, createJob, createOverride } from '../../services/syncApi';
import { DEFAULT_ENVIRONMENT } from '../../constants/markets';

const formatValue = (v) => {
  if (v === null || v === undefined) return <span className="muted">—</span>;
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'object') return <pre>{JSON.stringify(v, null, 2)}</pre>;
  return String(v);
};

const SkuDetail = () => {
  const { sku, market } = useParams();
  const [searchParams] = useSearchParams();
  const environment = searchParams.get('environment') || DEFAULT_ENVIRONMENT;
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await getDiffDetail(sku, market, environment);
      setDetail(r);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, [sku, market, environment]);

  useEffect(() => { load(); }, [load]);

  const triggerCompare = async () => {
    setBusy(true);
    try {
      const r = await createJob({ store_code: market, environment, mode: 'compare', skus: [sku], force_fresh: true, priority: 10 });
      window.location.href = `/jobs/history/${r.job_id}`;
    } catch (e) {
      alert(`Failed to enqueue: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const addOverride = async (field) => {
    const value = prompt(`Override ${field.field_name} (current desired: ${JSON.stringify(field.desired)})\nEnter JSON value (e.g. "Active" or true):`);
    if (value === null) return;
    let parsed;
    try { parsed = JSON.parse(value); }
    catch { parsed = value; }
    try {
      await createOverride({
        sku,
        market_code: market,
        bc_target: field.bc_target,
        bc_field_name: field.field_name,
        forced_value: parsed,
      });
      load();
    } catch (e) {
      alert(`Override failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  if (error) return <div className="error">Error: {error}</div>;
  if (!detail) return <div>Loading...</div>;

  return (
    <div className="sku-detail">
      <header>
        <h1>{sku} <small>({market}{environment !== DEFAULT_ENVIRONMENT ? ` · ${environment}` : ''})</small></h1>
        <button type="button" onClick={triggerCompare} disabled={busy}>Compare now</button>
      </header>

      <section className="card">
        <h2>Status</h2>
        <p>
          <span className={`status status-${detail.diff.status}`}>{detail.diff.status}</span>
          {' '}BC product: <strong>{detail.diff.bc_product_id || 'missing'}</strong>
          {' '}Last compared: {detail.diff.last_compared_at ? new Date(detail.diff.last_compared_at).toLocaleString() : '—'}
          {' '}Snapshot fetched: {detail.diff.snapshot_fetched_at ? new Date(detail.diff.snapshot_fetched_at).toLocaleString() : '—'}
        </p>
        {detail.diff.last_error && <p className="error">Last error: {detail.diff.last_error}</p>}
      </section>

      {detail.shared_with?.length > 0 && (
        <section className="card warn-card">
          <h2>Shared BigCommerce product</h2>
          <p>
            This SKU shares BC product <strong>{detail.diff.bc_product_id}</strong> with {detail.shared_with.length} other SKU(s):
          </p>
          <ul>
            {detail.shared_with.map((s) => (
              <li key={s.sku}>
                <Link to={`/jobs/config/diffs/${market}/${encodeURIComponent(s.sku)}`}>{s.sku}</Link>
                {' '}<small className="muted">strategy: {s.sync_strategy || 'not set'}</small>
              </li>
            ))}
          </ul>
          <p className="muted">Mutations to this BC product affect all of them. Configure <code>sync_strategy</code> upstream.</p>
        </section>
      )}

      <section className="card">
        <h2>Fields ({detail.fields.length})</h2>
        <table>
          <thead><tr><th>Field</th><th>Target</th><th>Status</th><th>Desired</th><th>Actual</th><th>Actions</th></tr></thead>
          <tbody>
            {detail.fields.map((f) => (
              <tr key={f.field_name}>
                <td>{f.field_name}</td>
                <td>{f.bc_target}</td>
                <td><span className={`status status-${f.status}`}>{f.status}</span></td>
                <td>{formatValue(f.desired)}</td>
                <td>{formatValue(f.actual)}</td>
                <td>
                  {(f.bc_target === 'core' || f.bc_target === 'custom_field') && (
                    <button type="button" onClick={() => addOverride(f)}>Override</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default SkuDetail;
